import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import type { Tables } from '@/types/database';
import { ok, err, type ApiResponse } from './_base';

export type ExtractoBancario = Tables<'extractos_bancarios'>;
export type ExtractoLinea = Tables<'extractos_lineas'>;

// ═══════════════════════════════════════════════════════════════════════════
// Row normalizada que viaja al RPC procesar_extracto_bancario
// ═══════════════════════════════════════════════════════════════════════════
export interface ExtractoRowNormalizado {
  fila: number;
  fecha: string; // YYYY-MM-DD
  descripcion: string | null;
  observaciones: string | null;
  referencia: string | null;
  /** Positivo = ingreso · Negativo = egreso · 0 = se ignora (saldo intermedio) */
  monto_neto: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Parser genérico de extracto bancario.
//
// Soporta 3 variantes del campo monto (el usuario arma el xlsx según su banco):
//   A) Columnas "DEBITO" + "CREDITO" separadas, ambas positivas.
//   B) Columnas "DEBITO" + "CREDITO" separadas pero con signo (débitos
//      con "-" delante). El parser igual funciona: toma valor absoluto.
//   C) Columna única "MONTO" con signo (+ ingreso, − egreso).
//
// Columnas reconocidas (matching case-insensitive + sin tildes):
//   - "FECHA" → date
//   - "DESCRIPCION" / "CONCEPTO" → descripcion
//   - "OBSERVACIONES" / "REFERENCIA DEL BANCO" → observaciones
//   - "REFERENCIA" / "NRO OPERACION" / "COMPROBANTE" → referencia
//   - "DEBITO" → monto egreso (abs)
//   - "CREDITO" → monto ingreso (abs)
//   - "MONTO" / "IMPORTE" → monto con signo (variante C)
//
// Filas sin fecha o con monto 0 se descartan silenciosamente.
// ═══════════════════════════════════════════════════════════════════════════
export interface ParseResult {
  rows: ExtractoRowNormalizado[];
  descartadas: number;
  advertencias: string[];
}

const HEADER_MAP: Record<keyof RawRow, (norm: string) => boolean> = {
  fecha: (n) => n === 'fecha' || n === 'fecha valor' || n === 'fecha operacion',
  descripcion: (n) =>
    n === 'descripcion' || n === 'concepto' || n === 'detalle' || n === 'movimiento',
  observaciones: (n) =>
    n === 'observaciones' ||
    n === 'observacion' ||
    n.startsWith('ref banco') ||
    n === 'referencia del banco',
  referencia: (n) =>
    n === 'referencia' ||
    n === 'comprobante' ||
    n.startsWith('nro ') ||
    n.startsWith('numero '),
  debito: (n) => n === 'debito' || n === 'debe' || n === 'egresos',
  credito: (n) => n === 'credito' || n === 'haber' || n === 'ingresos',
  monto: (n) => n === 'monto' || n === 'importe' || n === 'monto neto',
};

interface RawRow {
  fecha?: unknown;
  descripcion?: unknown;
  observaciones?: unknown;
  referencia?: unknown;
  debito?: unknown;
  credito?: unknown;
  monto?: unknown;
}

function normalizeHeader(h: string): string {
  return h
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .replace(/\./g, '')
    .trim();
}

function matchHeader(h: string): keyof RawRow | null {
  const n = normalizeHeader(h);
  for (const [key, test] of Object.entries(HEADER_MAP)) {
    if (test(n)) return key as keyof RawRow;
  }
  return null;
}

/** Normaliza un valor de celda a número, tolerando string con separadores AR. */
function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  // Formato AR: 1.234.567,89 → 1234567.89. Formato US: 1,234,567.89.
  // Detectamos por la ÚLTIMA ocurrencia de . o , — esa es la decimal.
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let cleaned = s.replace(/\s/g, '');
  if (lastDot > lastComma) {
    // US: coma miles, punto decimal
    cleaned = cleaned.replace(/,/g, '');
  } else if (lastComma > lastDot) {
    // AR: punto miles, coma decimal
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function toIsoDate(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  // Fecha serial de Excel (número de días desde 1900-01-01 con bug de 1900)
  if (typeof v === 'number') {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const ms = excelEpoch.getTime() + v * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  if (!s) return null;
  // DD/MM/YYYY o DD-MM-YYYY
  const arMatch = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (arMatch) {
    const day = arMatch[1]!.padStart(2, '0');
    const month = arMatch[2]!.padStart(2, '0');
    let year = arMatch[3]!;
    if (year.length === 2) year = Number(year) >= 70 ? `19${year}` : `20${year}`;
    return `${year}-${month}-${day}`;
  }
  // YYYY-MM-DD directo
  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]!.padStart(2, '0')}-${isoMatch[3]!.padStart(2, '0')}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

/**
 * Parsea un File xlsx o csv a ExtractoRowNormalizado[]. Corre en el browser.
 */
export async function parseExtractoFile(file: File): Promise<ParseResult> {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const first = wb.SheetNames[0];
  if (!first) throw new Error('El archivo no tiene hojas.');
  const sheet = wb.Sheets[first];
  if (!sheet) throw new Error('Hoja inválida.');
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: null,
    raw: true,
  });

  const advertencias: string[] = [];
  const rows: ExtractoRowNormalizado[] = [];
  let descartadas = 0;

  raw.forEach((rawRow, idx) => {
    const fila = idx + 2; // fila 1 = headers
    const parsed: RawRow = {};
    for (const [h, v] of Object.entries(rawRow)) {
      const key = matchHeader(h);
      if (!key) continue;
      parsed[key] = v;
    }

    const fecha = toIsoDate(parsed.fecha);
    if (!fecha) {
      descartadas++;
      return;
    }

    const debito = toNumber(parsed.debito);
    const credito = toNumber(parsed.credito);
    const montoUnico = toNumber(parsed.monto);

    let monto_neto = 0;
    if (montoUnico !== null && montoUnico !== 0) {
      // Variante C: columna única con signo
      monto_neto = montoUnico;
    } else if (credito !== null && credito !== 0 && (debito === null || debito === 0)) {
      // Variante A/B: crédito
      monto_neto = Math.abs(credito);
    } else if (debito !== null && debito !== 0 && (credito === null || credito === 0)) {
      // Variante A/B: débito
      monto_neto = -Math.abs(debito);
    } else if (debito !== null && debito !== 0 && credito !== null && credito !== 0) {
      // Ambas llenas → raro. El neto es (credito - |debito|).
      monto_neto = Math.abs(credito) - Math.abs(debito);
      advertencias.push(`Fila ${fila}: débito y crédito llenos, se tomó el neto.`);
    }

    if (monto_neto === 0) {
      descartadas++;
      return;
    }

    rows.push({
      fila,
      fecha,
      descripcion: toStringOrNull(parsed.descripcion),
      observaciones: toStringOrNull(parsed.observaciones),
      referencia: toStringOrNull(parsed.referencia),
      monto_neto: Math.round(monto_neto * 100) / 100,
    });
  });

  if (rows.length === 0) {
    advertencias.push(
      'No se detectaron filas con fecha + monto válidos. Revisá los encabezados del archivo.',
    );
  }

  return { rows, descartadas, advertencias };
}

function toStringOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Template descargable — incluye todas las columnas soportadas, el user
// adapta según su banco.
// ═══════════════════════════════════════════════════════════════════════════
export async function downloadExtractoTemplate(): Promise<void> {
  const XLSX = await import('xlsx');
  const HEADERS = [
    'FECHA',
    'DESCRIPCION',
    'OBSERVACIONES',
    'REFERENCIA',
    'DEBITO',
    'CREDITO',
    'MONTO',
  ];
  const ws = XLSX.utils.aoa_to_sheet([
    HEADERS,
    [
      '15/04/2026',
      'TRANSF. CREDICOOP',
      'Cobro 0011-0018 ABRIL',
      'OP123456',
      null,
      '25000.00',
      null,
    ],
    ['16/04/2026', 'COMISION MENSUAL', null, null, '350.00', null, null],
    [
      '16/04/2026',
      'TRANSF. A MERCADOPAGO',
      'alias: manaxer.mp',
      'OP123500',
      null,
      null,
      '-5000.00',
    ],
  ]);
  ws['!cols'] = HEADERS.map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Extracto');

  // Hoja de instrucciones
  const ins = XLSX.utils.aoa_to_sheet([
    ['MANAXER · Modelo de extracto bancario'],
    [],
    ['Columnas obligatorias:'],
    ['  · FECHA (DD/MM/AAAA o AAAA-MM-DD)'],
    ['  · Alguna de las variantes de monto (ver abajo)'],
    [],
    ['Columnas opcionales:'],
    ['  · DESCRIPCION o CONCEPTO'],
    ['  · OBSERVACIONES (aquí suele venir el código AAAA-EEEE del edificio)'],
    ['  · REFERENCIA (nro de operación / comprobante)'],
    [],
    ['Variantes de monto aceptadas:'],
    ['  A) Columnas DEBITO y CREDITO separadas, ambas positivas.'],
    ['  B) Igual que A pero débitos con signo negativo.'],
    ['  C) Columna única MONTO con signo (+ ingreso / − egreso).'],
    [],
    ['El parser detecta automáticamente cuál usaste. Podés borrar las columnas'],
    ['que no apliquen a tu banco.'],
    [],
    ['Tip: el formato del código del edificio en observaciones es AAAA-EEEE'],
    ['(ej: 0011-0018 = admin 11, edificio 18). Con eso, el sistema imputa'],
    ['automáticamente la cobranza al edificio correcto.'],
  ]);
  ins['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, ins, 'Instrucciones');

  XLSX.writeFile(wb, 'manaxer-modelo-extracto-bancario.xlsx');
}

// ═══════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════

export async function procesarExtracto(
  empresaId: string,
  cajaId: string,
  archivoNombre: string,
  rows: ExtractoRowNormalizado[],
): Promise<
  ApiResponse<{
    extracto_id: string;
    total: number;
    auto_codigo: number;
    auto_transferencia: number;
    fuzzy: number;
    sin_match: number;
    duplicadas: number;
  }>
> {
  const { data, error } = await supabase.rpc('procesar_extracto_bancario', {
    p_empresa_id: empresaId,
    p_caja_id: cajaId,
    p_archivo_nombre: archivoNombre,
    p_rows: rows as unknown as import('@/types/database').Json,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudo procesar el extracto.');
  return ok(first);
}

// ═══════════════════════════════════════════════════════════════════════════
// V2 chunked — TODO-MDC-22
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tamaño por defecto de cada chunk. 150 filas por chunk × ~5 lookups
 * indexados c/u (~0.5ms cada uno con `idx_comprobantes_match_fuzzy`)
 * = ~0.4s server-side por chunk + overhead red. Total para 3000 filas:
 * 20 chunks × ~1s = ~20s con feedback en vivo.
 *
 * Si en el futuro el dataset crece (10k+ comprobantes históricos) y
 * cada chunk tarda más, bajar a 100. La heurística: cada chunk debe
 * cerrar bajo el statement_timeout de `authenticated` (8s) con margen.
 */
export const CHUNK_SIZE_DEFAULT = 150;

export interface ChunkProgress {
  /** UUID del extracto creado al primer chunk. */
  extractoId: string;
  /** Chunks completados (0..total). */
  chunksProcesados: number;
  /** Total de chunks que se van a procesar. */
  chunksTotal: number;
  /** Filas integradas (después de descontar duplicadas). */
  totalAcumulado: number;
  autoCodigo: number;
  autoTransferencia: number;
  fuzzy: number;
  sinMatch: number;
  duplicadas: number;
  estado: string;
  /** ETA en milisegundos según ritmo medido hasta ahora. */
  etaMs: number | null;
  /** Filas totales a procesar (para barra). */
  filasTotales: number;
  /** Filas procesadas (cumulativo, sin descartar duplicadas). */
  filasProcesadas: number;
}

export interface ChunkRunOptions {
  /** Cancela el procesamiento. Si se aborta, los chunks ya integrados
   *  quedan persistidos pero el extracto se descarta vía
   *  `abandonarExtractoProcesando`. */
  signal?: AbortSignal;
  /** Callback de progreso después de cada chunk. */
  onProgress?: (p: ChunkProgress) => void;
  /** Tamaño de chunk. Default 150 (ver CHUNK_SIZE_DEFAULT). */
  chunkSize?: number;
  /** Reintentos por chunk antes de tirar error. Default 3. */
  maxRetries?: number;
}

class AbortError extends Error {
  constructor() {
    super('Procesamiento cancelado por el usuario.');
    this.name = 'AbortError';
  }
}

/**
 * TODO-MDC-22 — orquestador cliente del procesamiento chunked.
 *
 * Recibe TODAS las filas parseadas. Las parte en chunks de 150 (configurable)
 * y los serializa contra el RPC `procesar_extracto_chunk`. Cada chunk:
 *   - Persiste sus líneas en `extractos_lineas`.
 *   - Actualiza acumulados en `extractos_bancarios`.
 *   - Devuelve métricas que `onProgress` reporta a la UI.
 *
 * Resiliencia:
 *   - Cada chunk hace hasta `maxRetries` (3 default) intentos con backoff
 *     exponencial. Si el chunk N falla definitivamente, los chunks 0..N-1
 *     quedan persistidos — el usuario puede:
 *       a) reanudar (no implementado en esta primera versión, queda
 *          como mejora si lo pedís),
 *       b) descartar con `abandonarExtractoProcesando`.
 *   - Si `signal.aborted` → throw AbortError + descarta el extracto
 *     parcial automáticamente.
 *
 * Compatibilidad:
 *   - El RPC del último chunk marca `estado='parseado'`. La UI siguiente
 *     (ConciliacionDialog) lee normalmente las líneas y procede igual
 *     que con el flujo legacy.
 */
export async function processExtractoInChunks(
  empresaId: string,
  cajaId: string,
  archivoNombre: string,
  rows: ExtractoRowNormalizado[],
  opts: ChunkRunOptions = {},
): Promise<
  ApiResponse<{
    extracto_id: string;
    total: number;
    auto_codigo: number;
    auto_transferencia: number;
    fuzzy: number;
    sin_match: number;
    duplicadas: number;
    chunks_total: number;
    duracion_ms: number;
  }>
> {
  if (rows.length === 0) {
    return err('VALIDATION', 'No hay filas válidas para procesar.');
  }
  const chunkSize = Math.max(50, Math.min(opts.chunkSize ?? CHUNK_SIZE_DEFAULT, 500));
  const maxRetries = opts.maxRetries ?? 3;
  const totalChunks = Math.ceil(rows.length / chunkSize);
  const tStart = Date.now();

  let extractoId: string | null = null;
  let acc = {
    chunksProcesados: 0,
    totalAcumulado: 0,
    autoCodigo: 0,
    autoTransferencia: 0,
    fuzzy: 0,
    sinMatch: 0,
    duplicadas: 0,
    estado: 'procesando',
  };

  function checkAborted() {
    if (opts.signal?.aborted) throw new AbortError();
  }

  try {
    for (let i = 0; i < totalChunks; i++) {
      checkAborted();
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, rows.length);
      const chunk = rows.slice(start, end);
      const esUltimo = i === totalChunks - 1;

      // Reintentos con backoff exponencial. Causas típicas: timeout de
      // red, error transient de Supabase. NO reintentar errores de
      // validación o tenancy guard (los detectamos por código).
      let lastErr: { code: string; message: string } | null = null;
      let success = false;
      // Snapshot del extractoId para esta iteración. El cast a `string`
      // es seguro porque el RPC plpgsql acepta NULL en su firma — el
      // generator de types emite la columna como non-nullable.
      const currentExtractoIdArg: string = (extractoId ?? null) as unknown as string;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        checkAborted();
        const rpcResult: {
          data: Array<{
            extracto_id: string;
            chunks_procesados: number;
            total_acumulado: number;
            auto_codigo: number;
            auto_transferencia: number;
            fuzzy: number;
            sin_match: number;
            duplicadas: number;
            estado: string;
          }> | null;
          error: { code?: string; message: string } | null;
        } = await supabase.rpc('procesar_extracto_chunk', {
          p_extracto_id: currentExtractoIdArg,
          p_empresa_id: empresaId,
          p_caja_id: cajaId,
          p_archivo_nombre: archivoNombre,
          p_chunk_idx: i,
          p_chunks_total: totalChunks,
          p_rows_chunk: chunk as unknown as import('@/types/database').Json,
          p_es_ultimo: esUltimo,
        });
        const { data, error } = rpcResult;
        if (!error && data) {
          const first = data[0];
          if (first) {
            extractoId = first.extracto_id;
            acc = {
              chunksProcesados: first.chunks_procesados,
              totalAcumulado: first.total_acumulado,
              autoCodigo: first.auto_codigo,
              autoTransferencia: first.auto_transferencia,
              fuzzy: first.fuzzy,
              sinMatch: first.sin_match,
              duplicadas: first.duplicadas,
              estado: first.estado,
            };
            success = true;
            break;
          }
        }
        lastErr = {
          code: error?.code ?? 'RPC_FAILED',
          message: error ? toUserMessage(error) : 'Respuesta vacía del servidor.',
        };
        // Errores no-retry: validación, permisos, etc.
        if (error?.code === 'P0001' || error?.code === '42501') break;
        // Backoff: 500ms, 1s, 2s
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt - 1)));
        }
      }
      if (!success) {
        // Limpieza: dejar el extracto colgado en 'procesando' hasta
        // que el usuario decida descartar o reanudar manualmente.
        return err(
          lastErr?.code ?? 'CHUNK_FAILED',
          `Falló el chunk ${i + 1}/${totalChunks}. ${lastErr?.message ?? ''}`.trim(),
        );
      }

      // Reportar progreso a la UI
      const filasProcesadas = end;
      const elapsed = Date.now() - tStart;
      const avgPerRow = filasProcesadas > 0 ? elapsed / filasProcesadas : 0;
      const restantes = rows.length - filasProcesadas;
      const etaMs = restantes > 0 ? Math.round(avgPerRow * restantes) : 0;

      opts.onProgress?.({
        extractoId: extractoId!,
        chunksProcesados: acc.chunksProcesados,
        chunksTotal: totalChunks,
        totalAcumulado: acc.totalAcumulado,
        autoCodigo: acc.autoCodigo,
        autoTransferencia: acc.autoTransferencia,
        fuzzy: acc.fuzzy,
        sinMatch: acc.sinMatch,
        duplicadas: acc.duplicadas,
        estado: acc.estado,
        etaMs,
        filasTotales: rows.length,
        filasProcesadas,
      });
    }
  } catch (e) {
    if (e instanceof AbortError && extractoId) {
      // Limpiar extracto parcial si el usuario canceló.
      await abandonarExtractoProcesando(extractoId).catch(() => {});
      return err('ABORTED', 'Procesamiento cancelado.');
    }
    throw e;
  }

  if (!extractoId) {
    return err('RPC_EMPTY', 'No se pudo crear el extracto.');
  }

  return ok({
    extracto_id: extractoId,
    total: acc.totalAcumulado,
    auto_codigo: acc.autoCodigo,
    auto_transferencia: acc.autoTransferencia,
    fuzzy: acc.fuzzy,
    sin_match: acc.sinMatch,
    duplicadas: acc.duplicadas,
    chunks_total: totalChunks,
    duracion_ms: Date.now() - tStart,
  });
}

/**
 * Limpia un extracto que quedó colgado en estado 'procesando' (porque el
 * usuario canceló a mitad o se cortó la conexión). Idempotente.
 */
export async function abandonarExtractoProcesando(
  extractoId: string,
): Promise<ApiResponse<{ borrado: boolean; lineas_borradas?: number; motivo?: string }>> {
  const { data, error } = await supabase.rpc('abandonar_extracto_procesando', {
    p_extracto_id: extractoId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(data as { borrado: boolean; lineas_borradas?: number; motivo?: string });
}

/**
 * Lista extractos en estado 'procesando' que el usuario abandonó. Útil
 * para mostrar un banner "tenés un extracto incompleto, ¿continuar o
 * descartar?" cuando el usuario vuelve a la página.
 *
 * Filtra los más viejos que 1 hora — esos casi seguro fueron abandonos
 * reales y no procesos en curso de otra pestaña.
 */
export async function listExtractosProcesando(
  empresaId: string,
): Promise<ApiResponse<ExtractoBancario[]>> {
  const { data, error } = await supabase
    .from('extractos_bancarios')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('estado', 'procesando')
    .order('procesando_iniciado_at', { ascending: false });
  if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  return ok(data ?? []);
}

/**
 * Lista extractos en estado 'parseado' para una caja: ya se analizaron pero
 * el usuario no aplicó la conciliación. Útil para reanudar revisiones que
 * quedaron a medio camino (cierre accidental del browser, refresh).
 */
export async function listExtractosParseados(
  empresaId: string,
  cajaId: string,
): Promise<ApiResponse<ExtractoBancario[]>> {
  const { data, error } = await supabase
    .from('extractos_bancarios')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('caja_id', cajaId)
    .eq('estado', 'parseado')
    .order('subido_at', { ascending: false });
  if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  return ok(data ?? []);
}

export interface DecisionLinea {
  linea_id: string;
  decision: 'aceptado' | 'rechazado' | 'ignorado';
  edificio_id?: string;
  administracion_id?: string;
  comprobante_id?: string;
  caja_contraparte_id?: string;
  categoria_id?: string;
  nota?: string;
}

export async function aplicarConciliacion(
  extractoId: string,
  decisiones: DecisionLinea[],
): Promise<
  ApiResponse<{
    movimientos_creados: number;
    pendientes_id: number;
    rechazadas: number;
  }>
> {
  const { data, error } = await supabase.rpc('aplicar_conciliacion', {
    p_extracto_id: extractoId,
    p_decisiones: decisiones as unknown as import('@/types/database').Json,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudieron aplicar las decisiones.');
  return ok(first);
}

// ═══════════════════════════════════════════════════════════════════════════
// V2 · Decisiones tipadas (Capa 6 · migración 0068)
// ═══════════════════════════════════════════════════════════════════════════

/** Los 7 tipos de decisión que la UI ofrece por línea. */
export type DecisionTipo =
  | 'auto' // aceptar el match que trajo el parser
  | 'gasto' // egreso genérico (categoría opcional)
  | 'gasto_bancario' // egreso con categoría "Gastos bancarios"
  | 'mov_entre_cuentas' // transferencia entre cajas propias
  | 'mov_no_identificado' // movimiento con estado pendiente_id
  | 'pac_admin' // ingreso → imputación a administración
  | 'imputar_edificio'; // ingreso → N imputaciones a comprobantes + remanente

export interface ImputacionComprobante {
  comprobante_id: string;
  monto_imputado: number;
}

/**
 * Payload de decisión para el RPC aplicar_conciliacion_v2.
 * Shape unificado: para aceptar se manda `decision_tipo`; para rechazar/ignorar
 * se manda `decision` (compat con v1).
 */
export type DecisionLineaV2 =
  | {
      linea_id: string;
      decision: 'rechazado' | 'ignorado';
      nota?: string;
    }
  | {
      linea_id: string;
      decision_tipo: DecisionTipo;
      nota?: string;
      descripcion_override?: string;
      // Específicos por tipo — la RPC valida server-side.
      categoria_id?: string; // gasto
      caja_contraparte_id?: string; // mov_entre_cuentas
      administracion_id?: string; // pac_admin
      edificio_id?: string; // imputar_edificio
      imputaciones?: ImputacionComprobante[]; // imputar_edificio
      remanente_admin_id?: string; // imputar_edificio
      remanente_monto?: number; // imputar_edificio
    };

export async function aplicarConciliacionV2(
  extractoId: string,
  decisiones: DecisionLineaV2[],
): Promise<
  ApiResponse<{
    movimientos_creados: number;
    imputaciones_creadas: number;
    pendientes_id: number;
    rechazadas: number;
    por_tipo: Record<string, number>;
  }>
> {
  const { data, error } = await supabase.rpc('aplicar_conciliacion_v2', {
    p_extracto_id: extractoId,
    p_decisiones: decisiones as unknown as import('@/types/database').Json,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudieron aplicar las decisiones.');
  return ok(first as never);
}

/**
 * Borra un extracto en estado 'parseado' junto con sus líneas.
 * Se usa al cancelar/cerrar el modal sin aplicar — así los análisis testeo no
 * ensucian la BD. Un extracto 'aplicado' no se puede descartar (ya generó
 * movimientos reales).
 */
export async function descartarExtracto(
  extractoId: string,
): Promise<ApiResponse<{ borrado: boolean; motivo: string | null }>> {
  const { data, error } = await supabase.rpc('descartar_extracto', {
    p_extracto_id: extractoId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudo descartar el extracto.');
  return ok(first as { borrado: boolean; motivo: string | null });
}

/**
 * Re-matchea líneas (típicamente duplicadas) contra la cascada del motor.
 * Las líneas pasadas se actualizan en BD: pueden cambiar de match_type y
 * recibir destino. Devuelve el resultado por línea para el reporte.
 *
 * Chunkeamos en bloques de 50 para reportar progreso al UI.
 */
export interface RematchResult {
  linea_id: string;
  match_type_anterior: string;
  match_type_nuevo: string;
  destino_label: string | null;
}

export async function rematchLineasExtractoChunked(
  extractoId: string,
  lineaIds: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<ApiResponse<RematchResult[]>> {
  const CHUNK = 50;
  const out: RematchResult[] = [];
  for (let i = 0; i < lineaIds.length; i += CHUNK) {
    const slice = lineaIds.slice(i, i + CHUNK);
    const { data, error } = await supabase.rpc('rematch_lineas_extracto', {
      p_extracto_id: extractoId,
      p_lineas_ids: slice as unknown as never,
    });
    if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
    for (const r of (data ?? []) as unknown as Array<Record<string, unknown>>) {
      out.push({
        linea_id: String(r.linea_id),
        match_type_anterior: String(r.match_type_anterior ?? ''),
        match_type_nuevo: String(r.match_type_nuevo ?? ''),
        destino_label: (r.destino_label as string) ?? null,
      });
    }
    onProgress?.(Math.min(i + CHUNK, lineaIds.length), lineaIds.length);
  }
  return ok(out);
}

/**
 * Devuelve los IDs de las líneas del extracto cuyo hash ya existe en
 * movimientos. Esas líneas, al aplicar, harán UPDATE del mov existente
 * en lugar de crear uno nuevo → NO incrementan el saldo. Útil para que
 * el saldo proyectado del UI coincida con el saldo real post-aplicar.
 */
export async function lineasQueImputaranExistente(
  extractoId: string,
): Promise<ApiResponse<Set<string>>> {
  const { data, error } = await supabase.rpc('lineas_extracto_imputaran_existente', {
    p_extracto_id: extractoId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(new Set((data ?? []).map((r: { linea_id: string }) => String(r.linea_id))));
}

/**
 * Deshace un extracto en estado 'aplicado'. Borra los movimientos creados
 * por la conciliación, resetea las líneas a 'pendiente' y vuelve el extracto
 * al estado 'parseado'. Útil cuando el usuario detecta errores tras aplicar.
 *
 * Nota: las imputaciones a movimientos pre-existentes (del histórico) NO
 * se revierten automáticamente — quedan los destinos asignados. Si querés
 * limpieza fina, editar el movimiento manualmente.
 */
export async function revertirExtracto(
  extractoId: string,
): Promise<ApiResponse<{ movimientos_borrados: number; lineas_reseteadas: number }>> {
  const { data, error } = await supabase.rpc('revertir_extracto', {
    p_extracto_id: extractoId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('NO_DATA', 'La RPC no devolvió resultado');
  return ok({
    movimientos_borrados: Number(
      (first as { movimientos_borrados: number }).movimientos_borrados ?? 0,
    ),
    lineas_reseteadas: Number(
      (first as { lineas_reseteadas: number }).lineas_reseteadas ?? 0,
    ),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Borrador (TODO-MDC-41 · mig 0143)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Persiste el avance parcial del usuario sin aplicar. La RPC reemplaza el
 * borrador anterior — el cliente debe mandar el set completo de decisiones
 * que tiene en pantalla.
 *
 * Sólo funciona sobre extractos en estado 'parseado'. Marca el extracto
 * con tiene_borrador=true para mostrar banner al reabrir.
 */
export async function guardarBorradorConciliacion(
  extractoId: string,
  decisiones: DecisionLineaV2[],
): Promise<ApiResponse<{ lineas_guardadas: number; actualizado_at: string }>> {
  const { data, error } = await supabase.rpc('guardar_borrador_conciliacion', {
    p_extracto_id: extractoId,
    p_decisiones: decisiones as unknown as import('@/types/database').Json,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudo guardar el borrador.');
  return ok({
    lineas_guardadas: Number(first.lineas_guardadas ?? 0),
    actualizado_at: String(first.actualizado_at ?? ''),
  });
}

/**
 * Descarta el borrador: limpia decisiones pendientes y baja el flag.
 * Las líneas vuelven a su estado post-parser (sin decisión).
 */
export async function descartarBorradorConciliacion(
  extractoId: string,
): Promise<ApiResponse<{ lineas_limpiadas: number }>> {
  const { data, error } = await supabase.rpc('descartar_borrador_conciliacion', {
    p_extracto_id: extractoId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) return err('RPC_EMPTY', 'No se pudo descartar el borrador.');
  return ok({ lineas_limpiadas: Number(first.lineas_limpiadas ?? 0) });
}

export async function listExtractos(
  empresaId: string,
): Promise<ApiResponse<ExtractoBancario[]>> {
  // Sólo extractos efectivamente procesados — los 'parseado' son análisis en
  // curso o abandonados (se auto-descartan al cerrar el modal sin aplicar)
  // y no deben aparecer en el historial oficial.
  const { data, error } = await supabase
    .from('extractos_bancarios')
    .select('*')
    .eq('empresa_id', empresaId)
    .eq('estado', 'aplicado')
    .order('subido_at', { ascending: false });
  if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  return ok(data ?? []);
}

export async function getLineasExtracto(
  extractoId: string,
): Promise<ApiResponse<ExtractoLinea[]>> {
  const { data, error } = await supabase
    .from('extractos_lineas')
    .select('*')
    .eq('extracto_id', extractoId)
    .order('fila_excel');
  if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  return ok(data ?? []);
}

export interface DestinoLabels {
  admins: Map<string, { codigo: string; nombre: string }>;
  edificios: Map<string, { codigo: string; nombre: string; admin_codigo: string | null }>;
  comprobantes: Map<
    string,
    { tipo: string; punto_venta: number; numero: number | null; total: number }
  >;
}

/**
 * Carga en batch los nombres / códigos de admins, edificios y comprobantes
 * referenciados por las líneas del extracto. Devuelve diccionarios para que
 * la UI muestre la columna "Destino previsto" sin queries N+1.
 *
 * IMPORTANTE: PostgREST chunkea sólo hasta cierto largo de URL (~8KB). Con
 * >300 UUIDs en `in()`, la query falla silenciosamente. Por eso partimos
 * los IDs en chunks de 100 y juntamos los resultados.
 */
export async function getDestinoLabels(
  lineas: ExtractoLinea[],
): Promise<ApiResponse<DestinoLabels>> {
  const adminIds = [
    ...new Set(
      lineas.map((l) => l.administracion_id_match).filter((x): x is string => !!x),
    ),
  ];
  const edifIds = [
    ...new Set(lineas.map((l) => l.edificio_id_match).filter((x): x is string => !!x)),
  ];
  const compIds = [
    ...new Set(lineas.map((l) => l.comprobante_id_match).filter((x): x is string => !!x)),
  ];

  const adminMap = new Map<string, { codigo: string; nombre: string }>();
  const edifMap = new Map<
    string,
    { codigo: string; nombre: string; admin_codigo: string | null }
  >();
  const compMap = new Map<
    string,
    { tipo: string; punto_venta: number; numero: number | null; total: number }
  >();

  const CHUNK = 100;
  const chunkOf = <T>(arr: T[]): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += CHUNK) out.push(arr.slice(i, i + CHUNK));
    return out;
  };

  // 1) admins (los necesitamos primero para que edif sepa el codigo de su admin)
  for (const slice of chunkOf(adminIds)) {
    const { data, error } = await supabase
      .from('administraciones')
      .select('id, codigo, nombre')
      .in('id', slice);
    if (error) return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
    for (const a of data ?? [])
      adminMap.set(a.id, { codigo: a.codigo, nombre: a.nombre });
  }

  // 2) edificios y comprobantes en paralelo, cada uno chunkeado
  const edifPromises = chunkOf(edifIds).map((slice) =>
    supabase
      .from('edificios')
      .select('id, codigo, nombre, administracion_id')
      .in('id', slice),
  );
  const compPromises = chunkOf(compIds).map((slice) =>
    supabase
      .from('comprobantes')
      .select('id, tipo, punto_venta, numero, total')
      .in('id', slice),
  );
  const [edifResults, compResults] = await Promise.all([
    Promise.all(edifPromises),
    Promise.all(compPromises),
  ]);

  for (const r of edifResults) {
    if (r.error) return err(r.error.code ?? 'LIST_FAILED', toUserMessage(r.error));
    for (const e of r.data ?? []) {
      const adm = e.administracion_id ? adminMap.get(e.administracion_id) : null;
      edifMap.set(e.id, {
        codigo: e.codigo,
        nombre: e.nombre,
        admin_codigo: adm?.codigo ?? null,
      });
    }
  }
  for (const r of compResults) {
    if (r.error) return err(r.error.code ?? 'LIST_FAILED', toUserMessage(r.error));
    for (const c of r.data ?? []) {
      compMap.set(c.id, {
        tipo: c.tipo,
        punto_venta: Number(c.punto_venta ?? 0),
        numero: c.numero ?? null,
        total: Number(c.total ?? 0),
      });
    }
  }

  return ok({ admins: adminMap, edificios: edifMap, comprobantes: compMap });
}
